import {FormConfig} from '../../form/form.config';
import {FormModelText} from '../../form/form.model.text';
import {FormModel} from '../../form/form.model.abstract';
import {UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import {FormModelRadio} from '../../form/form.model.radio';
import {ValidationService} from '../../services/validation.service';
import {FormModelHidden} from '../../form/form.model.hidden';
import {EingabeStepComponent} from './eingabe.step.component';
import {FormModelNumber} from '../../form/form.model.number';
import {WpcheckConstants} from '../wpcheck.constants';
import {FormModelPercent} from '../../form/form.model.percent';
import {WpcheckAnfrage} from '../anfrage/wpcheck.anfrage';

/**
 * Configuration for the input of the first chapter in the wpcheck advisor.
 * @inheritDoc
 */
export class EingabeForm extends FormConfig {

  /**
   * The options for some fields that are dynamic, depending on other form fields
   * option heizungstyp['1-2familienhaus'] is a fallback for user with old result and pdf link
   */
  static readonly options: any = {
    heizungstyp: {
      mehrfamilienhaus: ['zentral', 'dezentral', 'dezentral2'],
      'einfamilienhaus': ['zentral', 'dezentral2'],
      'zweifamilienhaus': ['zentral', 'dezentral2'],
      '1-2familienhaus': ['zentral', 'dezentral2'],
    }
  };

  /**
   * konfiguration for the validation of window types
   */
  static readonly fensterVerglasungsartValidators: { [key: string]: { min: number, max: number } } = {
    einfach: {min: 0, max: 1978},
    zweifachverbund: {min: 0, max: 1994},
    zweifachwaermeschutz: {min: 1995, max: 0},
    dreifachwaermeschutz: {min: 2002, max: 0}
  };

  /**
   * (input) data of the user
   */
  stepComponent: EingabeStepComponent;

  /**
   * some data is needed here for validation
   */
  public anfrage: WpcheckAnfrage;

  /**
   * @inheritDoc
   *
   * ATTENTION: Some of these values/fields are also used in WpcheckController.php. Check changes also there.
   * ATTENTION: Some of these values are alos used in ErgebnisComponent.moFieldMappings, check them
   *
   * @protected
   */
  protected fields: FormModel[] = [
    // basisinformationen
    new FormModelRadio('nutzungstyp', ['hauseigentuemer', 'wohnungseigentuemer', 'hausverwalter', 'mieter'],
      [Validators.required], null, null, false),
    new FormModelRadio('gebaeude_denkmalschutz', ['ja', 'nein'], [Validators.required], null, null, false),
    new FormModelRadio('gebaeudetyp', ['einfamilienhaus', 'zweifamilienhaus', 'mehrfamilienhaus'], [Validators.required], null, null, false),
    // value of heizungstyp must be mapped in the backend for dezentral2
    new FormModelRadio('heizungstyp', ['zentral', 'dezentral', 'dezentral2'], [Validators.required], null, null, false),
    new FormModelRadio('energietraeger_name', ['erdgas', 'heizoel', 'fluessiggas', 'fernwaerme', 'holzpellets'],
      [Validators.required], null, null, false),
    (new FormModelText('gebaeudebaujahr', [Validators.required, ValidationService.buildingYear], null, null, false, 'number')).setClasses('small-field'),
    (new FormModelText('postleitzahl', [Validators.required, ValidationService.postalCode], null, null, false)).setClasses('small-field'),
    new FormModelHidden('ort'),
    (new FormModelText('gebaeude_flaeche', [Validators.required, Validators.min(0)], 'm2', null, false, 'number'))
      .setClasses('small-field'),
    new FormModelRadio('keller_status', ['nicht_vorhanden', 'vorhanden', 'vorhanden_beheizt'], [Validators.required], null, null, false),
    (new FormModelRadio('gebaeude_lage', ['freistehend', 'mitte', 'ecke'], [Validators.required], null, null, true)).setIconPath('/wpc/icon-gebaeude_lage_'),
    (new FormModelRadio('gebaeude_grundriss', ['kompakt', 'komplex'], [Validators.required], null, null, false)).setIconPath('/wpc/icon-gebaeude_grundriss_'),
    new FormModelNumber('gebaeude_stockwerke', [Validators.required, Validators.min(WpcheckConstants.FORM_FLOOR_MIN_MFH),
      Validators.max(WpcheckConstants.FORM_FLOOR_MAX_MFH), ValidationService.allowIntegerOnly], null, WpcheckConstants.FORM_FLOOR_MIN_MFH, false),
    (new FormModelRadio('gebaeude_dachform', ['spitzdach', 'flachdach', 'pultdach'], [Validators.required], null, null, false)).setIconPath('/wpc/icon-gebaeude_dachform_'),
    (new FormModelRadio('gebaeude_flaechenanteil_fenster', ['niedrig', 'mittel', 'hoch'],
      [Validators.required], null, null, false)).setIconPath('/wpc/icon-gebaeude_flaechenanteil_fenster_'),

    // Heizung & WW
    new FormModelRadio('fussbodenheizung_vorhanden', ['fussbodenheizung', 'mischbetrieb', 'konvektoren'],
      [Validators.required], null, null, false),
    new FormModelRadio('leitungssystem', ['zweirohr', 'einrohr'], [Validators.required], null, null, false),
    new FormModelRadio('vorlauftemperatur_bekannt', ['ja', 'nein'], [Validators.required], null, null, false),
    (new FormModelText('vorlauftemperatur', [Validators.required,
        Validators.min(WpcheckConstants.FORM_VORLAUFTEMPERATUR_MIN), Validators.max(WpcheckConstants.FORM_VORLAUFTEMPERATUR_MAX)],
      'deg', null, false, 'number')).setInactive(),
    new FormModelRadio('hydraulikabgleich_zustand', ['unbekannt', 'nicht_durchgefuehrt', 'durchgefuehrt'],
      [Validators.required], null, null, false),
    (new FormModelText('hydraulikabgleich_baujahr', [Validators.required, ValidationService.buildingYear],
      null, null, false, 'number')).setErrorId('buildingyear').setClasses('small-field').setInactive(),
    new FormModelRadio('warmwasser_bereitungsart', ['zentral', 'zentral2', 'dezentral'], [Validators.required], null, 'zentral', false),
    new FormModelRadio('zirkulationsleitung_vorhanden', ['ja', 'nein'], [Validators.required], null, null, false),

    // Massnahmen
    new FormModelRadio('fassade_zustand', ['unbekannt', 'nicht_gedaemmt', 'gedaemmt'], [Validators.required], null, null, false),
    (new FormModelText('fassade_baujahr', [Validators.required, ValidationService.buildingYear], null, null, false, 'number')).setErrorId('buildingyear').setClasses('small-field').setInactive(),
    new FormModelRadio('gebaeude_dachausbau', ['ja', 'nein'], [Validators.required], null, null, false),
    new FormModelRadio('dach_zustand', ['unbekannt', 'nicht_gedaemmt', 'gedaemmt'], [Validators.required], null, null, false),
    (new FormModelText('dach_baujahr', [Validators.required, ValidationService.buildingYear], null, null, false, 'number')).setClasses('small-field').setInactive(),
    new FormModelRadio('obergeschoss_zustand', ['unbekannt', 'nicht_gedaemmt', 'gedaemmt'], [Validators.required], null, null, false),
    (new FormModelText('obergeschoss_baujahr', [Validators.required, ValidationService.buildingYear],
      null, null, false, 'number')).setErrorId('buildingyear').setClasses('small-field').setInactive(),
    new FormModelRadio('kellerdecke_zustand', ['unbekannt', 'nicht_gedaemmt', 'gedaemmt'], [Validators.required], null, null, false),
    (new FormModelText('kellerdecke_baujahr', [Validators.required, ValidationService.buildingYear],
      null, null, false, 'number')).setErrorId('buildingyear').setClasses('small-field').setInactive(),
    (new FormModelRadio('fenster_verglasungsart', ['einfach', 'zweifachverbund', 'zweifachwaermeschutz', 'dreifachwaermeschutz'],
      [Validators.required], null, null, true)).setIconPath('/wpc/icon-fenster_verglasungsart_'),
    new FormModelRadio('fenster_zustand', ['unbekannt', 'nicht_erneuert', 'erneuert'], [Validators.required], null, null, false),
    (new FormModelText('fenster_baujahr', [Validators.required, ValidationService.buildingYear], null, null, false, 'number')).setErrorId('windowyear').setInactive(),
    new FormModelRadio('fenster_flaechenanteil_alle', ['ja', 'nein'], [Validators.required], null, null, false),
    (new FormModelPercent('flaechenanteil', ['fenster_flaechenanteil', 'fenster_flaechenanteil_2'],
      [Validators.required], [100, 0], false)).setInactive(),
    (new FormModelRadio('fenster_verglasungsart_2', ['einfach', 'zweifachverbund', 'zweifachwaermeschutz', 'dreifachwaermeschutz'],
      [Validators.required], null, null, true)).setIconPath('/wpc/icon-fenster_verglasungsart_').setInactive(),
    (new FormModelRadio('fenster_zustand_2', ['unbekannt', 'nicht_erneuert', 'erneuert'], [Validators.required],
      null, null, false)).setInactive(),
    (new FormModelText('fenster_baujahr_2', [Validators.required, ValidationService.buildingYear], null, null, false, 'number')).setErrorId('windowyear').setInactive()
  ];

  /**
   * @inheritDoc
   * @protected
   */
  protected formValidators = [
    EingabeForm.windowBuildingYear(this),
  ];

  /**
   * List of formfields where changes of the value, change other fields.
   */
  fieldsForFormChanges: string[] = [
    'nutzungstyp',
    'gebaeudetyp',
    'heizungstyp',
    'gebaeudebaujahr',
    'keller_status',
    'vorlauftemperatur_bekannt',
    'warmwasser_bereitungsart',
    'hydraulikabgleich_zustand',
    'dach_zustand',
    'obergeschoss_zustand',
    'fassade_zustand',
    'kellerdecke_zustand',
    'fenster_flaechenanteil_alle',
    'fenster_zustand',
    'fenster_zustand_2'
  ];

  /**
   * @inheritDoc
   * @protected
   */
  protected groups = {
    'basisinformationen/1': ['nutzungstyp'],
    'basisinformationen/2': ['gebaeudetyp'],
    'basisinformationen/3': ['gebaeude_denkmalschutz'],
    'basisinformationen/4': ['heizungstyp'],
    'basisinformationen/5': ['energietraeger_name'],
    'basisinformationen/6': ['gebaeudebaujahr'],
    'basisinformationen/7': ['postleitzahl', 'ort'],
    'basisinformationen/8': ['gebaeude_flaeche'],
    'basisinformationen/9': ['keller_status'],
    'basisinformationen/10': ['gebaeude_lage'],
    'basisinformationen/11': ['gebaeude_grundriss'],
    'basisinformationen/12': ['gebaeude_stockwerke'],
    'basisinformationen/13': ['gebaeude_dachform'],
    'basisinformationen/14': ['gebaeude_flaechenanteil_fenster'],

    'heizung-und-wasser/1': ['fussbodenheizung_vorhanden'],
    'heizung-und-wasser/2': ['leitungssystem'],
    'heizung-und-wasser/3': ['vorlauftemperatur_bekannt', 'vorlauftemperatur'],
    'heizung-und-wasser/4': ['hydraulikabgleich_zustand', 'hydraulikabgleich_baujahr'],
    'heizung-und-wasser/5': ['warmwasser_bereitungsart'],
    'heizung-und-wasser/6': ['zirkulationsleitung_vorhanden'],

    'massnahmen/1': ['fassade_zustand', 'fassade_baujahr'],
    'massnahmen/2': ['gebaeude_dachausbau'],
    'massnahmen/3': ['dach_zustand', 'dach_baujahr', 'obergeschoss_zustand', 'obergeschoss_baujahr'],
    'massnahmen/4': ['kellerdecke_zustand', 'kellerdecke_baujahr'],
    'massnahmen/5': ['fenster_verglasungsart', 'fenster_zustand', 'fenster_baujahr', 'fenster_flaechenanteil_alle', 'flaechenanteil', 'fenster_verglasungsart_2',
      'fenster_zustand_2', 'fenster_baujahr_2']
  };

  /**
   * returns the list of possible options for field fenster_verglasungsart based on the building year.
   * @param gebaeudebaujahr
   */
  static getVerglasungsoptionsForBaujahr(gebaeudebaujahr): string[] {
    return Object.keys(EingabeForm.fensterVerglasungsartValidators).filter(type => {
      return EingabeForm.fensterVerglasungsartValidators[type].max === 0 ||
        gebaeudebaujahr <= EingabeForm.fensterVerglasungsartValidators[type].max;
    });
  }

  /**
   * validates the building year of a window based on its type.
   * @param formConfig
   */
  static windowBuildingYear(formConfig: EingabeForm): ValidatorFn {
    return (formGroup: UntypedFormGroup) => {
      const validations = EingabeForm.fensterVerglasungsartValidators;
      const typeField = 'fenster_verglasungsart';
      const yearField = 'fenster_baujahr';
      const statusField = 'fenster_zustand';

      // validate both field sets, first and second
      for (const postfix of ['', '_2']) {
        // if the window type and a renewal year is given, check the date agains min and max of the type
        if (formGroup.controls[typeField + postfix]?.value && formGroup.controls[statusField + postfix]?.value === 'erneuert' && formGroup.controls[yearField + postfix]?.value) {
          const year = parseInt(formGroup.controls[yearField + postfix].value, 10);
          const type = formGroup.controls[typeField + postfix].value;

          if (year < validations[type].min || (validations[type].max > 0 && year > validations[type].max)) {
            return {windowBuildingYear: false};
          }
        }

        // if the window type is not replaces, check the building year agains the min year of the type
        const buildingYear = formConfig?.anfrage?.userInput?.gebaeudebaujahr;
        if (buildingYear && formGroup.controls[statusField + postfix]?.value && formGroup.controls[typeField + postfix]?.value) {
          const type = formGroup.controls[typeField + postfix].value;
          const min = validations[type].min;
          if (formGroup.controls[statusField + postfix]?.value === 'nicht_erneuert' && buildingYear < min) {
            return {windowBuildingYear: false};
          }
        }
      }

      // everything is fine, of the fields are not theire, of they have no values
      return null;
    };
  }

  /**
   * Sets stepComponent
   * @param stepComponent
   */
  setStepComponent(stepComponent: EingabeStepComponent): void {
    this.stepComponent = stepComponent;
  }

  /**
   * Any time a field is updated, this method is called.
   * @param values
   */
  updateFields(values: { [key: string]: any }): any {
    // change value of field gebaeudetyp if there is still the value '1-2familienhaus' from old requests/results
    if(values.hasOwnProperty('gebaeudetyp') && values.gebaeudetyp === '1-2familienhaus') {
      values.gebaeudetyp = 'einfamilienhaus';
    }

    // changes based on field 'gebaeudetyp'
    if (values.hasOwnProperty('gebaeudetyp')) {
      // set options for heating type
      this.setOptions('heizungstyp', EingabeForm.options.heizungstyp[values.gebaeudetyp]);

      // reset older selection (if available)
      if(EingabeForm.options.heizungstyp[values.gebaeudetyp]?.indexOf(values.heizungstyp) === -1) {
        values.heizungstyp = null;
      }

      if (values.gebaeudetyp !== 'mehrfamilienhaus') {
        this.setFieldValidators('gebaeude_flaeche', [Validators.required, ValidationService.buildingAreaOneFamilyHouse]);
        this.getField('gebaeude_flaeche').setErrorId('gebaeude_flaeche.efh');
        this.setFieldValidators('gebaeude_stockwerke',
          [Validators.required, Validators.min(WpcheckConstants.FORM_FLOOR_MIN_EFH), Validators.max(WpcheckConstants.FORM_FLOOR_MAX_EFH), ValidationService.allowIntegerOnly]);
      }
      else {
        this.setFieldValidators('gebaeude_flaeche', [Validators.required, ValidationService.buildingAreaMultiFamilyHouse]);
        this.getField('gebaeude_flaeche').setErrorId('gebaeude_flaeche.mfh');
        this.setFieldValidators('gebaeude_stockwerke',
          [Validators.required, Validators.min(WpcheckConstants.FORM_FLOOR_MIN_MFH), Validators.max(WpcheckConstants.FORM_FLOOR_MAX_MFH), ValidationService.allowIntegerOnly]);
      }
    }

    // changes based on field 'heizungstyp'
    if (values.hasOwnProperty('heizungstyp')) {
      if (values.heizungstyp === 'dezentral') {
        this.deactivateField('energietraeger_name');
        values.energietraeger_name = 'erdgas';
      }
      else if (values.heizungstyp === 'dezentral2') {
        this.deactivateField('energietraeger_name');
        values.energietraeger_name = 'strom';
      }
      else {
        this.activateField('energietraeger_name');
      }
      if (values.hasOwnProperty('energietraeger_name') && values.energietraeger_name === 'strom' && values.heizungstyp === 'zentral') {
        values.energietraeger_name = null;
        this.form.controls.energietraeger_name?.setValue(null, {onlySelf: true});
      }
    }

    // gebaeudetyp + heizungstyp
    if (values.hasOwnProperty('gebaeudetyp') && values.hasOwnProperty('heizungstyp')) {
      if (values.gebaeudetyp !== 'mehrfamilienhaus' && values.heizungstyp === 'dezentral2') {
        this.deactivateField('fussbodenheizung_vorhanden');
        this.deactivateField('vorlauftemperatur_bekannt');
        this.deactivateField('leitungssystem');
        this.deactivateField('hydraulikabgleich_zustand');
        this.deactivateField('hydraulikabgleich_baujahr');
        this.deactivateField('warmwasser_bereitungsart');
        values.warmwasser_bereitungsart = null;
        values.fussbodenheizung_vorhanden = null;
        values.vorlauftemperatur_bekannt = null;
        values.vorlauftemperatur = null;
        values.leitungssystem = null;
        values.hydraulikabgleich_zustand = null;
        values.hydraulikabgleich_baujahr = null;
      }
      else {
        this.activateField('fussbodenheizung_vorhanden');
        this.activateField('vorlauftemperatur_bekannt');
        this.activateField('leitungssystem');
        this.activateField('hydraulikabgleich_zustand');
        this.activateField('hydraulikabgleich_baujahr');
        this.activateField('warmwasser_bereitungsart');
      }
    }

    // vorlauftemperatur_bekannt
    if (values.hasOwnProperty('vorlauftemperatur_bekannt')) {
      if (values.vorlauftemperatur_bekannt === 'ja') {
        this.activateField('vorlauftemperatur');
      }
      else {
        this.deactivateField('vorlauftemperatur');
        values.vorlauftemperatur = null;
      }
    }

    // warmwasser_bereitungsart + gebaeudetyp + heizungstyp
    if (values.hasOwnProperty('warmwasser_bereitungsart') && values.hasOwnProperty('gebaeudetyp') && values.hasOwnProperty('heizungstyp')) {
      if ((values.warmwasser_bereitungsart === 'zentral' || values.warmwasser_bereitungsart === 'zentral2')
        && values.gebaeudetyp !== 'mehrfamilienhaus' && values.heizungstyp === 'zentral') {
        this.activateField('zirkulationsleitung_vorhanden');
      }
      else {
        this.deactivateField('zirkulationsleitung_vorhanden');
        values.zirkulationsleitung_vorhanden = null;
      }
    }

    // add validators on some year fields to the minimum of the building year
    if (values.hasOwnProperty('gebaeudebaujahr')) {
      // set options of fenster_verglasungsart
      const options = EingabeForm.getVerglasungsoptionsForBaujahr(values.gebaeudebaujahr);
      this.setOptions('fenster_verglasungsart', options);
      this.setOptions('fenster_verglasungsart_2', options);

      ['hydraulikabgleich_baujahr', 'dach_baujahr', 'obergeschoss_baujahr', 'fassade_baujahr', 'kellerdecke_baujahr', 'fenster_baujahr', 'fenster_baujahr_2'].forEach(fieldName => {
        this.setFieldValidators(fieldName, [
          Validators.required, ValidationService.buildingYear, Validators.min(values.gebaeudebaujahr * 1)
        ]);
      });
    }

    // fussbodenheizung_vorhanden
    if (values.hasOwnProperty('fussbodenheizung_vorhanden') && values.fussbodenheizung_vorhanden) {
      if (values.fussbodenheizung_vorhanden === 'fussbodenheizung') {
        this.deactivateField('leitungssystem');
        values.leitungssystem = null;
      }
      else {
       this.activateField('leitungssystem');
      }
    }

    // *_zustand -> de/activate *_baujahr
    ['hydraulikabgleich_', 'dach_', 'obergeschoss_', 'fassade_', 'kellerdecke_', 'fenster_'].forEach(fieldPrefix => {
      if (values.hasOwnProperty(fieldPrefix + 'zustand')) {
        if (values[fieldPrefix + 'zustand'] === 'erneuert' || values[fieldPrefix + 'zustand'] === 'durchgefuehrt' || values[fieldPrefix + 'zustand'] === 'gedaemmt') {
          this.activateField(fieldPrefix + 'baujahr');
        }
        else {
          this.deactivateField(fieldPrefix + 'baujahr');
          values[fieldPrefix + 'baujahr'] = null;
          this.form.controls[fieldPrefix + 'baujahr']?.setValue(null, {onlySelf: true});
        }
      }
    });

    // gebaeude_dachform -> gebaeude_dachausbau
    if (values.hasOwnProperty('gebaeude_dachform')) {
      if (values.gebaeude_dachform === 'spitzdach') {
        this.activateField('gebaeude_dachausbau');
      }
      else {
        this.deactivateField('gebaeude_dachausbau');
        values.gebaeude_dachausbau = null;
      }
    }

    // based on gebaeude_dachform show/hide dach_* and obergeschoss_*
    if (values.hasOwnProperty('gebaeude_dachform') && values.hasOwnProperty('gebaeude_dachausbau')) {
      if (values.gebaeude_dachform === 'spitzdach' && values.gebaeude_dachausbau === 'nein') {
        this.deactivateField('dach_zustand');
        this.activateField('obergeschoss_zustand');
        values.dach_zustand = null;
      }
      else {
        this.activateField('dach_zustand');
        this.deactivateField('obergeschoss_zustand');
        values.obergeschoss_zustand = null;
      }
    }

    // based on keller_status, show/hide keller_status
    if (values.hasOwnProperty('keller_status')) {
      if (values.keller_status === 'vorhanden') {
        this.activateField('kellerdecke_zustand');
      }
      else {
        this.deactivateField('kellerdecke_zustand');
        this.deactivateField('kellerdecke_baujahr');
        values.kellerdecke_zustand = null;
        values.kellerdecke_baujahr = null;
      }
    }

    // second window types
    if (values.hasOwnProperty('fenster_flaechenanteil_alle')) {
      if (values.fenster_flaechenanteil_alle === 'nein') {
        ['flaechenanteil', 'fenster_verglasungsart_2', 'fenster_zustand_2', 'fenster_flaechenanteil_2']
          .forEach(fieldName => this.activateField(fieldName));
      }
      else if (values.fenster_flaechenanteil_alle === 'ja') {
        values.fenster_flaechenanteil = 100;
        values.fenster_flaechenanteil_2 = 0;
        ['flaechenanteil', 'fenster_verglasungsart_2', 'fenster_zustand_2', 'fenster_flaechenanteil_2'].forEach(fieldName => {
          this.deactivateField(fieldName);
          values[fieldName] = null;
        });
      }
    }

    if (values.hasOwnProperty('fenster_zustand_2')) {
      if (values.fenster_zustand_2 === 'erneuert') {
        this.activateField('fenster_baujahr_2');
      }
      else {
        this.deactivateField('fenster_baujahr_2');
        values.fenster_baujahr_2 = null;
        this.form.controls.fenster_baujahr_2?.setValue(null, {onlySelf: true});
      }
    }

    return values;
  }

  /**
   * In this function all dynamic changes of the form is done.
   * Based on selection options or value other fields are shown/hidden, options changed and validators adapted.
   *
   * @inheritDoc
   * @param values
   * @protected
   */
  protected onSetData(values: { [key: string]: any }): void {
    this.updateFields(values);
  }

  /**
   * @inheritDoc
   * @protected
   */
  protected onChange() {
    // check if the form has some fields which value changes other fields.
    if (this.fieldsForFormChanges.find(field => this.form.get(field)) !== undefined) {
      const newValues = this.updateFields(this.getData());
      this.stepComponent.saveData(newValues);
    }
  }

}
